import { Controller } from "@hotwired/stimulus"

// The toggle-display controller is used to toggle the display of a specific element. This is done by adding or removing
// the hidden class from the target element. The target element to toggle is specified with a query selector in the
// element value. If you specify a name value then an event is dispatched on toggle that is named
// "toggle-display:toggled:{nameValue}".
//
// You can also specify optional targets for the main icon, displayed icon and hidden icon. These are then managed based
// on the state of the element. The hidden or displayed icons are shown in the corresponding states, the main icon is
// given the blue class when the element is visible.
export default class extends Controller {
  static get targets() {
    return ["mainIcon", "displayedIcon", "hiddenIcon"]
  }

  static get values() {
    return { element: String, name: String }
  }

  connect() {
    this.#findTargetElement()

    if (this.targetElement) {
      this.updateState()
    }
  }

  isHidden() {
    return this.targetElement.classList.contains("d-none")
  }

  updateState() {
    this.#verifyTargetElementPresence()

    if (this.isHidden()) {
      if (this.hasHiddenIconTarget) {
        this.hiddenIconTargets.forEach((icon) => icon.classList.add("d-none"))
      }

      if (this.hasDisplayedIconTarget) {
        this.displayedIconTargets.forEach((icon) => icon.classList.remove("d-none"))
      }

      if (this.hasMainIconTarget) {
        this.mainIconTarget.classList.remove("blue")
      }
    } else {
      if (this.hasHiddenIconTarget) {
        this.hiddenIconTargets.forEach((icon) => icon.classList.remove("d-none"))
      }

      if (this.hasDisplayedIconTarget) {
        this.displayedIconTargets.forEach((icon) => icon.classList.add("d-none"))
      }

      if (this.hasMainIconTarget) {
        this.mainIconTarget.classList.add("blue")
      }
    }

    if (this.nameValue) {
      this.dispatch(`toggled:${this.nameValue}`, { detail: { hidden: this.isHidden() } })
    }
  }

  toggle() {
    this.#verifyTargetElementPresence()

    if (this.isHidden()) {
      this.targetElement.classList.remove("d-none")
      this.element.setAttribute("aria-expanded", "true")
    } else {
      this.targetElement.classList.add("d-none")
      this.element.setAttribute("aria-expanded", "false")
    }

    this.updateState()
  }

  #verifyTargetElementPresence() {
    if (this.targetElement?.isConnected) {
      return
    }

    this.#findTargetElement()
  }

  #findTargetElement() {
    this.targetElement = this.element.querySelector(this.elementValue)

    if (!this.targetElement) {
      this.targetElement = document.querySelector(this.elementValue)
    }
  }
}
